import { SIJI_DATA } from '@entities/saju'
import { cn } from '@shared/lib/cn'
import { Button } from '@shared/ui/button'
import { HeroLayout } from '@shared/ui/hero-layout'
import { HiMiniCheckCircle, HiMiniChevronRight } from 'react-icons/hi2'

type FormStep = 'landing' | 1 | 2 | 3 | 4 | 5

interface FormValues {
  readonly name: string
  readonly gender: string
  readonly birthYear: string
  readonly birthMonth: string
  readonly birthDay: string
  readonly birthTime: string
  readonly isLunar: boolean
  readonly concerns: string[]
  readonly concernText: string
}

interface Step5ConfirmProps {
  readonly formValues: FormValues
  readonly onBack: () => void
  readonly onEdit: (step: FormStep) => void
  readonly isPending: boolean
  readonly saveInfo: boolean
  readonly onSaveInfoChange: (checked: boolean) => void
}

export function Step5Confirm({
  formValues,
  onBack,
  onEdit,
  isPending,
  saveInfo,
  onSaveInfoChange,
}: Step5ConfirmProps) {
  const v = formValues
  const name = v.name || '회원'
  const birthStr = v.birthYear
    ? `${v.birthYear}.${v.birthMonth.padStart(2, '0')}.${v.birthDay.padStart(2, '0')}`
    : ''
  const calendarType = v.isLunar ? '음력' : '양력'
  const timeLabel = SIJI_DATA.find((s) => s.value === v.birthTime)
  const genderLabel = v.gender === 'MALE' ? '남자' : v.gender === 'FEMALE' ? '여자' : ''
  const concernSummary = v.concernText || v.concerns.join(', ') || '-'

  const rows = [
    { label: '이름', value: name, editStep: 1 as FormStep },
    { label: '생년월일', value: `${birthStr} (${calendarType})`, editStep: 2 as FormStep },
    {
      label: '출생시간',
      value: timeLabel ? `${timeLabel.name} (${timeLabel.range})` : '모름',
      editStep: 3 as FormStep,
    },
    { label: '성별', value: genderLabel, editStep: 1 as FormStep },
    { label: '고민', value: concernSummary, editStep: 4 as FormStep, type: 'concern' as const },
  ]

  return (
    <HeroLayout
      characterImage="/character-assets/5_final_check_cleaned.png"
      progressStep={5}
      title="이대로 사주를 볼까요?"
      subtitle="수정하고 싶은 항목을 눌러주세요"
      onBack={onBack}
      gradientStop="70%"
      footer={
        <div className="flex flex-col gap-3">
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isPending}>
            사주 분석 시작하기
          </Button>
          <p className="text-center typo-footnote text-muted-foreground/60">
            입력한 정보는 사주 분석에만 사용되며, 안전하게 보호돼요.
          </p>
        </div>
      }
    >
      {/* Confirm Rows */}
      <div className="flex flex-col">
        {rows.map((row, i) => (
          <div key={row.label}>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between gap-1 rounded-lg px-3 py-1.5 transition-colors hover:bg-muted/20 active:bg-muted/20"
              onClick={() => {
                onEdit(row.editStep)
              }}
            >
              {row.type === 'concern' ? (
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <span className="typo-label text-muted-foreground">{row.label}</span>
                    <HiMiniChevronRight className="shrink-0 text-base text-muted-foreground" />
                  </div>
                  <p className="line-clamp-2 text-left typo-label text-foreground">{row.value}</p>
                </div>
              ) : (
                <>
                  <span className="shrink-0 typo-label text-muted-foreground">{row.label}</span>
                  <span className="flex min-w-0 items-center gap-1 typo-label text-foreground">
                    <span className="truncate">{row.value}</span>
                    <HiMiniChevronRight className="shrink-0 text-base text-muted-foreground" />
                  </span>
                </>
              )}
            </button>
            {i < rows.length - 1 && <div className="mx-3 my-1 border-b border-border" />}
          </div>
        ))}
      </div>

      {/* Save Info Checkbox */}
      <button
        type="button"
        className="mt-2.5 flex cursor-pointer items-center gap-1 px-1"
        onClick={() => {
          onSaveInfoChange(!saveInfo)
        }}
      >
        <HiMiniCheckCircle
          className={cn(
            'text-lg transition-colors',
            saveInfo ? 'text-primary' : 'text-muted-foreground/30',
          )}
        />
        <span className="typo-label text-muted-foreground">다음에도 이 정보 사용하기</span>
      </button>
    </HeroLayout>
  )
}
